// src/pages/import/declaracoes-aduaneiras/relatorios/RelatorioControleDADialog.tsx
import { Box, Grid, FormControl, Typography, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { makeStyles } from '@material-ui/core';
import { Form, Field } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useKeycloak } from '@react-keycloak/web';
import moment from 'moment';
import MultiSelectStyled from '../../../common/components/input/multiSelectStyled';
import QCXFinalDatePickerField from '../../../../shared-components/final-date-picker-field/QCXFinalDatePickerField';
import { KEYCLOAK_TOKEN_TIMEOUT } from '../../../../App';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import { useUnidadeNegocioGuard } from '../../../common/hooks/useUnidadeNegocioGuard';
import { useListClientes } from '../../../common/hooks/useListClientes';
import { downloadRelatorioControleDA } from './relatorioControleDA.helpers';
import { useState, useCallback, SyntheticEvent } from 'react';
import {
  selectUnidadesDeNegocioAssociadas,
  selectUnidadeSelecionada,
} from '../../../../features/usuario-logado/usuarioLogadoSlice';
import { fetchByFilter as fetchFollowUpsByClients } from '../../../../features/follow-up/followUpAPI';

interface Option { id: string|number; value: string; }

const useStyles = makeStyles({
  root: { '& .MuiDialog-paper': { minWidth: '60vw' } },
});

export default function RelatorioControleDADialog({
  title = 'Relatório Controle de DA',
  isOpen,
  closeModal,
}: { title?: string; isOpen: boolean; closeModal: () => void; }) {
  useUnidadeNegocioGuard();
  const classes = useStyles();

  const dispatch = useDispatch();
  const { keycloak } = useKeycloak();
  const { token } = keycloak;
  keycloak.updateToken(KEYCLOAK_TOKEN_TIMEOUT);

  const selectedUnit = useSelector(selectUnidadeSelecionada);
  const unidades = useSelector(selectUnidadesDeNegocioAssociadas);

  const [clientes] = useListClientes();

  const [selectedBusiness, setSelectedBusiness] = useState<Option[]>([]);
  const [selectedClients, setSelectedClients] = useState<Option[]>([]);
  const [followUpsOptions, setFollowUpsOptions] = useState<Option[]>([]);
  const [selectedProcesses, setSelectedProcesses] = useState<Option[]>([]);
  const [businessError, setBusinessError] = useState(false);
  const [isFetchingProcesses, setIsFetchingProcesses] = useState(false);

  const fetchProcesses = useCallback(async (cli: Option[]) => {
    try {
      setIsFetchingProcesses(true);
      const ids = cli.map(c => c.id).join(',');
      if (ids) {
        const resp = await fetchFollowUpsByClients([{ name: 'clientIds', value: ids }]);
        const list = (resp?.data || []).map((it: any) => ({
          id: it.id?.toString() || '',
          value: it.numero?.toString() || '[Sem número]',
        }));
        setFollowUpsOptions(list);
      } else {
        setFollowUpsOptions([]);
      }
    } finally {
      setIsFetchingProcesses(false);
    }
  }, []);

  const handleClientsChange = (e: SyntheticEvent, value: Option[]|null) => {
    const mapped: Option[] = [];
    value?.forEach(v => {
      const found = clientes.find(c => `${c.id}` === `${v.id}`);
      if (found?.id && !mapped.some(x => `${x.id}` === `${found.id}`)) {
        mapped.push({ id: `${found.id}`, value: `(${found.pessoa?.cnpj}) ${found.pessoa?.nome}` });
      }
    });
    setSelectedClients(mapped);
    setSelectedProcesses([]);
    fetchProcesses(mapped);
  };

  const handleSubmit = async (vals: any) => {
    keycloak.updateToken(KEYCLOAK_TOKEN_TIMEOUT);

    if (!selectedBusiness.length) {
      setBusinessError(true);
      return;
    }
    setBusinessError(false);

    if (!token || !selectedUnit) return;

    const inicio = vals.startDate ? moment(vals.startDate).format('YYYY-MM-DD') : '';
    const fim    = vals.endDate   ? moment(vals.endDate).format('YYYY-MM-DD')   : '';

    const tenants  = selectedBusiness.map(b => `${b.id}`);
    const clientes = selectedClients.map(c => `${c.id}`);
    const processos= selectedProcesses.map(p => `${p.id}`);

    const ret = await downloadRelatorioControleDA(
      token,
      selectedUnit,
      tenants,
      clientes,
      processos,
      inicio,
      fim
    );

    if ((ret as any)?.error) {
      dispatch(setErrorFeedback({ message: (ret as any).message }));
    } else {
      dispatch(setSuccessFeedback({ message: 'Relatório gerado com sucesso.' }));
      closeModal();
    }
  };

  return (
    <Dialog className={classes.root} open={isOpen} onClose={closeModal}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container>
          <Grid item lg={12} xs={12}>
            <Box sx={{ display:'flex', flexDirection:'column' }}>
              <Form
                onSubmit={handleSubmit}
                initialValues={{
                  startDate: '',
                  endDate: '',
                }}
                render={({ handleSubmit, values }) => (
                  <Box component="form" onSubmit={handleSubmit} mt={2} p={2} border={1} borderRadius={4} borderColor="grey.300">
                    <Typography variant="h6">Filtros</Typography>
                    <Grid container spacing={2}>
                      <Grid item sm={4} style={{ marginTop: 5 }}>
                        <FormControl fullWidth error={businessError}>
                          <MultiSelectStyled
                            disablePortal={false}
                            controlledValues={selectedBusiness}
                            options={unidades.map((u:any) => ({ id: u.id, value: u.pessoa?.nomeResumido }))}
                            onChangeAction={(_, v) => setSelectedBusiness(v || [])}
                            label={'Unidade de Negócio (Obrigatório)'}
                          />
                          {businessError && (
                            <Typography variant="body2" color="error">Este campo é obrigatório</Typography>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item sm={4} style={{ marginTop: 5 }}>
                        <FormControl fullWidth>
                          <MultiSelectStyled
                            disablePortal={false}
                            options={clientes.map((c:any) => ({
                              id: c.id ?? '',
                              value: `(${c.pessoa?.cnpj}) ${c.pessoa?.nome}`,
                            }))}
                            onChangeAction={(e:any, v:any) => {
                              handleClientsChange(e, v);
                              (values as any).clients = v;
                            }}
                            label={'Clientes (Opcional)'}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item sm={4} style={{ marginTop: 5 }}>
                        <FormControl fullWidth>
                          <MultiSelectStyled
                            disablePortal={false}
                            options={followUpsOptions}
                            onChangeAction={(_, v:any) => setSelectedProcesses(v || [])}
                            label={'Processos (Opcional)'}
                            placeholder={
                              selectedClients.length === 0
                                ? 'Selecione um Cliente'
                                : isFetchingProcesses ? 'Carregando...' : 'Selecionar Processos'
                            }
                            disabled={selectedClients.length === 0 || isFetchingProcesses}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item sm={4}>
                        <Field
                          name="startDate"
                          render={({ input }) => (
                            <QCXFinalDatePickerField
                              id="controle-da-start"
                              name={input.name}
                              label={'Vencimento — Início (Opcional)'}
                              format="DD/MM/YYYY"
                              placeholder="DD/MM/YYYY"
                              required={false}
                              validate={undefined}
                              onChange={(d:any) => input.onChange(d)}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item sm={4}>
                        <Field
                          name="endDate"
                          render={({ input }) => (
                            <QCXFinalDatePickerField
                              id="controle-da-end"
                              name={input.name}
                              label={'Vencimento — Fim (Opcional)'}
                              format="DD/MM/YYYY"
                              placeholder="DD/MM/YYYY"
                              required={false}
                              validate={undefined}
                              onChange={(d:any) => input.onChange(d)}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item sm={4} style={{ display:'flex', alignItems:'flex-end' }}>
                        <Button type="submit" variant="contained" color="secondary" fullWidth>
                          Buscar / Baixar
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box sx={{ p:2 }}>
          <Button variant="outlined" onClick={closeModal}>Fechar</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
